/*
 * The one agent entry point, in the sidebar footer below the bell. Always a
 * stack of agent faces; with signals the well tints red or green and carries
 * the count, pulsing twice when it rises. Running agents add a grey corner
 * spinner. Bounces once when a banner is pulled back into it.
 */
import { Loader2 } from "lucide-react";
import { useEffect, useMemo, useRef, type FC } from "react";

import { SidebarMenuButton, SidebarMenuItem } from "@/components/ui/sidebar";
import { useRunningTurnsStore } from "@/features/agents/runningTurnsStore";
import { cn } from "@/lib/utils";

import {
  countsTowardBadge,
  useAgentAttention
} from "../context/AgentAttentionProvider";
import { useAgentsTrayStore } from "../context/agentsTrayStore";
import { useAgentStackSeeds } from "../hooks/useAgentStackSeeds";
import { groupTone } from "../signalTone";
import { FaceStack } from "./FaceStack";

const WELL = {
  red: {
    well: "bg-danger-soft ring-1 ring-destructive",
    ring: "ring-danger-line",
    badge: "bg-destructive",
    pulse: "border-destructive"
  },
  green: {
    well: "bg-success-soft ring-1 ring-success",
    ring: "ring-success-line",
    badge: "bg-success",
    pulse: "border-success"
  }
} as const;

export const AgentsButton: FC = () => {
  const { items, count } = useAgentAttention();
  const running = useRunningTurnsStore((s) => s.turns.length);
  const seeds = useAgentStackSeeds();
  const open = useAgentsTrayStore((s) => s.open);
  const toggle = useAgentsTrayStore((s) => s.toggle);
  const gulp = useAgentsTrayStore((s) => s.gulp);
  const setAnchor = useAgentsTrayStore((s) => s.setAnchor);
  const counted = useMemo(
    () => items.filter((i) => countsTowardBadge(i)),
    [items]
  );
  const tone = groupTone(counted);
  const ref = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    setAnchor(ref.current);
    return () => setAnchor(null);
  }, [setAnchor]);

  // Pulse only when the count rises.
  const prev = useRef(count);
  const rose = count > prev.current;
  useEffect(() => {
    prev.current = count;
  }, [count]);
  const pulseKey = useRef(0);
  if (rose) pulseKey.current += 1;

  const label = count
    ? `Agents: ${count} need${count === 1 ? "s" : ""} attention`
    : "Agents";
  const w = tone ? WELL[tone] : null;

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        ref={ref}
        tooltip={label}
        aria-label={label}
        aria-expanded={open}
        isActive={open}
        data-testid="agents-button"
        data-agents-anchor=""
        onClick={toggle}
      >
        <span
          key={gulp}
          className={cn(
            "relative flex size-5 shrink-0 items-center justify-center rounded-md group-data-[collapsible=icon]:-mx-0.5",
            gulp && "signal-gulp",
            w?.well
          )}
        >
          {w ? (
            <span
              key={pulseKey.current}
              aria-hidden
              className={cn(
                "signal-pulse-ring pointer-events-none absolute inset-0 rounded-md border",
                w.pulse
              )}
            />
          ) : null}
          <FaceStack seeds={seeds} ringClassName={w?.ring ?? "ring-sidebar"} />
          {w ? (
            <span
              data-testid="agents-count"
              className={cn(
                "absolute -top-1 -right-1 flex h-3.5 min-w-3.5 items-center justify-center rounded-full px-1 text-xs leading-none font-bold text-white ring-cutout ring-sidebar",
                w.badge
              )}
            >
              {count > 9 ? "9+" : count}
            </span>
          ) : null}
          {running ? (
            <span
              className="absolute -right-1 -bottom-1 rounded-full bg-sidebar p-px text-muted-foreground"
              aria-label="Agents working"
            >
              <Loader2 className="size-2.5 animate-spin" />
            </span>
          ) : null}
        </span>
        <span>Agents</span>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
};
